import { WEAPONS } from '../sim/level';
import type { ExpeditionRoute } from '../sim/expedition';
import { MAX_DEPTH, routeRewardLabel, type ExpeditionSave } from './expedition';
import './expedition.css';

const element = <K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

function seedField(id: string): { label: HTMLLabelElement; input: HTMLInputElement } {
  const label = element('label', 'expedition-seed', 'Seed (optional)');
  const input = element('input', 'expedition-seed-input');
  input.id = id;
  input.type = 'text';
  input.maxLength = 64;
  input.placeholder = 'Random seed';
  input.autocomplete = 'off';
  input.addEventListener('keydown', (event) => { if (event.key !== 'Tab') event.stopPropagation(); });
  input.addEventListener('keyup', (event) => event.stopPropagation());
  label.htmlFor = id;
  label.append(input);
  return { label, input };
}

function button(label: string, action: () => void, className = ''): HTMLButtonElement {
  const result = element('button', className, label);
  result.type = 'button';
  result.addEventListener('click', action);
  return result;
}

export function mountExpeditionEntry(save: ExpeditionSave | null, onLaunch: (seed: string) => void, onResume: () => void): void {
  const start = document.getElementById('start');
  if (!start) return;
  document.getElementById('expedition-entry')?.remove();
  const entry = element('section', 'expedition-entry');
  entry.id = 'expedition-entry';
  entry.setAttribute('aria-label', 'Expedition');
  const heading = element('div', 'expedition-entry-title', 'EXPEDITION');
  const { label, input } = seedField('expedition-entry-seed');
  const actions = element('div', 'expedition-entry-actions');
  actions.append(button('NEW EXPEDITION', () => onLaunch(input.value)));
  if (save && !['lost', 'extracted'].includes(save.run.status)) actions.append(button('RESUME', onResume, 'expedition-secondary'));
  entry.append(heading, element('p', 'expedition-entry-hint', 'Face three bosses, keep upgrades, then extract or push on.'), label, actions);
  start.append(entry);
}

export function createExpeditionUi(actions: {
  onContinue(): void;
  onRoute(route: ExpeditionRoute): void;
  onExtract(): void;
  onNewRun(seed: string): void;
  onCampaign(): void;
}): { show(save: ExpeditionSave, persistent: boolean): void; hide(): void; setStatus(save: ExpeditionSave): void } {
  const panel = element('div', 'expedition-overlay');
  panel.id = 'expedition-panel';
  panel.hidden = true;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-labelledby', 'expedition-title');
  const content = element('div', 'expedition-content');
  panel.append(content);
  const status = element('div', 'expedition-status');
  status.id = 'expedition-status';
  status.hidden = true;
  status.setAttribute('aria-label', 'Expedition status');
  (document.getElementById('app') ?? document.body).append(status, panel);
  let previousFocus: HTMLElement | null = null;
  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const controls = Array.from(panel.querySelectorAll<HTMLElement>('button, input')).filter(control => control.getClientRects().length > 0);
    if (!controls.length) return;
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  return {
    setStatus(save) {
      const { run } = save;
      status.textContent = `BRIDGE ${run.depth} · ${run.route.toUpperCase()} · ${run.salvage} SALVAGE`;
      status.hidden = false;
    },
    hide() {
      panel.hidden = true;
      previousFocus?.focus();
      previousFocus = null;
    },
    show(save, persistent) {
      const { run } = save;
      const finished = run.status === 'lost' || run.status === 'extracted';
      const sector = Math.floor((run.depth - 1) / 3) + 1;
      previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      content.replaceChildren();
      content.classList.toggle('expedition-sector', run.status === 'sector');
      const title = element('h2', 'expedition-title', finished ? (run.status === 'lost' ? 'SQUAD WIPED' : 'EXTRACTION COMPLETE') : (run.status === 'sector' ? 'SECTOR SECURED' : 'BRIDGE SECURED'));
      title.id = 'expedition-title';
      content.append(title);
      content.append(element('p', 'expedition-meta', `SECTOR ${sector} · BRIDGE ${run.status === 'briefing' ? 2 : run.depth} · SEED ${run.seed}`));
      if (run.depth === 1 && run.status === 'debrief') {
        content.append(button('CONTINUE TO LEVEL 2', actions.onContinue));
        panel.hidden = false;
        content.querySelector<HTMLButtonElement>('button')?.focus();
        return;
      }
      if (run.depth === 1 && run.status === 'lost') {
        content.append(button('RETRY', () => actions.onNewRun(run.seed)));
        panel.hidden = false;
        content.querySelector<HTMLButtonElement>('button')?.focus();
        return;
      }
      if (run.status === 'briefing') title.textContent = 'LEVEL 2 BRIEFING';
      if (finished) {
        content.append(element('p', 'expedition-intro', `${run.lastReward} salvage banked ${run.status === 'lost' ? 'after defeat' : 'on extraction'}. Total banked: ${save.banked}.`));
        if (run.status === 'lost') content.append(element('p', 'expedition-intro', 'Your combat upgrades reset. Banked salvage stays.'));
        const { label, input } = seedField('expedition-new-seed');
        content.append(label);
        const choices = element('div', 'expedition-choices');
        choices.append(button('NEW EXPEDITION', () => actions.onNewRun(input.value)), button('CAMPAIGN', actions.onCampaign, 'expedition-secondary'));
        content.append(choices);
      } else {
        const nextBoss = ['Brute', 'Tank', 'Helicopter'][run.depth % 3];
        if (run.depth < MAX_DEPTH) content.append(element('p', 'expedition-intro', `Next bridge: ${run.depth + 1} · ${nextBoss}`));
        const facts = element('div', 'expedition-facts');
        facts.append(element('div', '', `${run.loadout.n} soldiers`), element('div', '', `${WEAPONS[run.loadout.weaponTier]?.label ?? 'SIDEARM MK I'} · ${run.loadout.ratePips} fire-rate boosts`), element('div', '', `${run.salvage} carried · ${save.banked} banked salvage`));
        content.append(facts);
        if (run.status === 'sector') {
          content.append(element('p', 'expedition-intro expedition-risk', 'Extract banks all carried salvage. Defeat keeps 25%.'), button(`EXTRACT · BANK ${run.salvage}`, actions.onExtract, 'expedition-extract'));
        }
        if (run.depth < MAX_DEPTH) {
          content.append(element('div', 'expedition-prompt', run.status === 'sector' ? 'OR PUSH ON' : 'CHOOSE YOUR ROUTE'));
          const choices = element('div', 'expedition-choices');
          for (const route of ['recruitment', 'armory'] as const) {
            const choice = button('', () => actions.onRoute(route), 'expedition-route');
            choice.append(element('strong', '', `${run.status === 'sector' ? 'PUSH ON: ' : ''}${route.toUpperCase()}`), element('span', '', routeRewardLabel(run.loadout, route)), element('span', '', route === 'recruitment' ? 'More rescue encounters' : 'More defended supply lanes'));
            choices.append(choice);
          }
          content.append(choices);
        }
        const leave = button(persistent ? 'SAVE & LEAVE' : 'LEAVE FOR CAMPAIGN', actions.onCampaign, 'expedition-secondary expedition-leave');
        leave.title = persistent ? 'Leave this expedition at its current checkpoint' : 'Leave this expedition for the campaign';
        content.append(leave);
      }
      if (!persistent) content.append(element('p', 'expedition-warning', 'This run is not saved across tabs.'));
      panel.hidden = false;
      content.querySelector<HTMLButtonElement>('button')?.focus();
    },
  };
}
